import * as React from 'react';
import { components as reactSelectComponents } from 'react-select';
import { Select } from '../../Form/Select';
import { brand } from '../../../theme/chakra';

export type SelectOption = { label: string; value: string };

/**
 * Selected chips take the same feather fill as the panel's other resting
 * surfaces, so a selection reads as a tag rather than as react-select's
 * default grey.
 */
const filterSelectStyles = {
  multiValue: (base: Record<string, unknown>) => ({
    ...base,
    backgroundColor: brand.feather,
    borderRadius: 4,
  }),
  multiValueLabel: (base: Record<string, unknown>) => ({
    ...base,
    color: brand.seafoam,
    fontWeight: 500,
  }),
  multiValueRemove: (base: Record<string, unknown>) => ({
    ...base,
    color: brand.seafoam,
    ':hover': { backgroundColor: brand.mint, color: brand.evergreen },
  }),
  // Room for the count badge that sits between the chips and the indicators.
  valueContainer: (base: Record<string, unknown>) => ({
    ...base,
    paddingRight: 0,
  }),
};

/**
 * Right-justified tally of how many options are selected.
 *
 * The control is one line tall and scrolls its chips, so past the first
 * selection you can no longer see how many you have without opening it. The
 * count lives in the indicators container, which is already pinned right,
 * and only appears once there's more than one — a "1" next to a single
 * visible chip would just be noise.
 */
const IndicatorsWithCount = (props: {
  selectProps?: { value?: unknown };
  children?: React.ReactNode;
}) => {
  const selected = props.selectProps?.value;
  const count = Array.isArray(selected) ? selected.length : 0;

  return (
    <reactSelectComponents.IndicatorsContainer
      {...(props as React.ComponentProps<
        typeof reactSelectComponents.IndicatorsContainer
      >)}
    >
      {count > 1 && (
        <span
          aria-label={`${count} selected`}
          style={{
            flexShrink: 0,
            alignSelf: 'center',
            marginRight: 2,
            padding: '1px 6px',
            borderRadius: 999,
            background: brand.seafoam,
            color: brand.canvas,
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.5,
          }}
        >
          {count}
        </span>
      )}
      {props.children}
    </reactSelectComponents.IndicatorsContainer>
  );
};

const filterSelectComponents = { IndicatorsContainer: IndicatorsWithCount };

/**
 * Dropdown that speaks `string[]` instead of react-select's option objects,
 * so the sidebar sections stay declarative and the filter state stays
 * URL-shaped — including the single-select dropdowns below, which keep the
 * same `string[]` shape capped at one element rather than a scalar. That
 * mirrors `visitConfirmations` in `useHealthCoachDashboard`: the control
 * enforces the cardinality, the state doesn't have to.
 *
 * Most dropdowns are multi-select — the brief's mockups show single-value
 * "All" dropdowns, but its stated goal is filters that combine into one real
 * question, and "High or Medium risk" is exactly that. `isMulti={false}`
 * (Providers, Coach, Care Model, User Type as of the Aug 2026 filter-logic
 * pass) opts a dropdown out where the team decided single-select is the
 * right question — e.g. one provider at a time, ANDed against Coach in
 * `matchesCareTeam` rather than OR'd against other providers.
 */
export const FilterSelect = ({
  placeholder,
  options,
  selected,
  onChange,
  isMulti = true,
}: {
  placeholder: string;
  options: SelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  isMulti?: boolean;
}) =>
  isMulti ? (
    <Select
      isMulti
      isClearable
      // Searching only earns its keep once the list outgrows a glance.
      isSearchable={options.length > 8}
      // The sidebar is a tall scrolling column, so its lower controls sit near
      // the bottom of the window and a menu that always opens downward gets cut
      // off by the viewport. `auto` flips it above the control when there isn't
      // room below.
      menuPlacement="auto"
      height={38}
      placeholder={placeholder}
      options={options}
      value={options.filter((o) => selected.includes(o.value))}
      onChange={(opts: readonly SelectOption[] | null) =>
        onChange((opts ?? []).map((o) => o.value))
      }
      styles={filterSelectStyles}
      components={filterSelectComponents}
    />
  ) : (
    <Select
      isMulti={false}
      isClearable
      isSearchable={options.length > 8}
      menuPlacement="auto"
      height={38}
      placeholder={placeholder}
      options={options}
      value={options.find((o) => selected.includes(o.value)) ?? null}
      onChange={(opt: SelectOption | null) =>
        onChange(opt ? [opt.value] : [])
      }
      styles={filterSelectStyles}
    />
  );
